import { CalendarDetailsVisibility, ID, Task } from '@griply/types';
import { getTaskCalendarSyncTargets, LinkedItemGoal } from '@griply/utils';
import {
  useExternalCalendarConnections,
  useSetTaskCalendarOverride,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useCallback, useMemo } from 'react';
import {
  CalendarEventSyncState,
  CalendarSyncStates,
  ToggleCalendarVisibility,
} from 'shared/components/ui/full-calendar-event';

export type CalendarTaskSync = {
  calendarSyncStates: CalendarSyncStates;
  onToggleCalendarVisibility: ToggleCalendarVisibility;
};

export const useCalendarTaskSync = (tasks: Task[]): CalendarTaskSync => {
  const connections = useExternalCalendarConnections();
  const goals = useGoals();
  const setTaskOverride = useSetTaskCalendarOverride();

  const calendarSyncStates = useMemo(() => {
    const goalsById = new Map<ID, LinkedItemGoal>(
      goals.map((goal) => [goal.id, goal]),
    );

    return tasks.reduce<Map<ID, CalendarEventSyncState>>((map, task) => {
      const targets = getTaskCalendarSyncTargets({
        task,
        connections,
        goalsById,
      });
      if (!targets.length) {
        return map;
      }

      const isHidden = targets.every(
        (target) => target.visibility === CalendarDetailsVisibility.Hidden,
      );
      map.set(task.id, { isHidden });
      return map;
    }, new Map());
  }, [tasks, connections, goals]);

  const onToggleCalendarVisibility = useCallback(
    (task: Task, isHidden: boolean) =>
      setTaskOverride(task, {
        detailsVisibility: isHidden
          ? CalendarDetailsVisibility.Visible
          : CalendarDetailsVisibility.Hidden,
      }),
    [setTaskOverride],
  );

  return useMemo(
    () => ({ calendarSyncStates, onToggleCalendarVisibility }),
    [calendarSyncStates, onToggleCalendarVisibility],
  );
};
